import { useState } from "react"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import MDProgress from "components/MDProgress"
import TimelineItem from "examples/Timeline/TimelineItem"
import SectionCard from "../SectionCard"
import { Button } from "../../ui"
import { useRefined } from "../../ui/refined"
import ScopedDataTable from "../ScopedDataTable"
import StatusBadge from "../StatusBadge"
import VendorComparisonModal from "../proposals/VendorComparisonModal"
import NoteCell from "./NoteCell"
import ProposalDeadlineActions from "../proposals/ProposalDeadlineActions"
import ProposalHistoryDialog from "../proposals/ProposalHistoryDialog"
import LinkActions, { proposalLink } from "../proposals/LinkActions"
import {
  formatVND,
  formatVNDSigned,
  formatDate,
  approvalModeLabel,
  formatQuorum,
  NO_QUOTE_LABEL,
  quotedItems,
  hasQuote,
  proposalTotal,
  PRICE_SOURCE_LABEL,
  formatWarranty,
  linePeriodLabel,
  LINE_PERIOD_COLOR,
} from "../../constants"

// Manager Order Review — MD-styled equivalents of the shared `OrderCards`
// pieces (which stay on the requester's tokens.css design so its own detail
// page doesn't fork visually). Same data shapes, same section order, just
// built from SectionCard / MDBox / MDProgress / TimelineItem so this screen
// reads as one system with Suppliers / Orders / Report / Budget.

const budgetHealthColor = (remaining, total) => {
  if (!total || total <= 0) return "secondary"
  const ratio = remaining / total
  if (ratio < 0.1) return "error"
  if (ratio < 0.2) return "warning"
  return "success"
}

const DOC_ICON = {
  contract: "history_edu",
  invoice: "receipt_long",
  delivery_note: "local_shipping",
  acceptance_certificate: "fact_check",
  payment_request: "description",
  budget_approval: "account_balance_wallet",
  price_approval: "sell",
}

// ── Budget Context ───────────────────────────────────────────────────────────
export const BudgetContextSection = ({ ctx, orderTotal }) => {
  if (!ctx) {
    return (
      <SectionCard title="Budget Context" icon="account_balance_wallet">
        <MDBox
          p={2}
          borderRadius="lg"
          sx={{ border: "1px solid", borderColor: "error.main", backgroundColor: "#fef2f2" }}
        >
          <MDBox display="flex" alignItems="flex-start" gap={1}>
            <Icon fontSize="small" color="error">
              warning
            </Icon>
            <MDBox>
              <MDTypography variant="button" color="error" fontWeight="bold" display="block">
                No active budget found
              </MDTypography>
              <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                This order was submitted without a usable budget context. It needs budget setup or
                approval-route repair before it can move normally through the queue.
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </SectionCard>
    )
  }

  const total = Number(ctx.total) || 0
  const remaining = Number(ctx.remaining) || 0
  const after =
    ctx.remainingAfterThisOrder != null
      ? Number(ctx.remainingAfterThisOrder)
      : remaining - (Number(orderTotal) || 0)
  // BE flags the budget itself as over-spent (see GET /order-requests/{id}
  // budgetContext) — no longer inferred from this order's own total.
  const over = !!ctx.isOverBudget
  const overSpent = Number(ctx.overSpentAmount) || 0
  const usedPct = total > 0 ? Math.max(0, Math.min(100, ((total - remaining) / total) * 100)) : 0
  const healthColor = budgetHealthColor(remaining, total)

  // "Total Budget" (ctx.total) is only this one month's own new grant, not
  // useful on its own once accumulateUnspent is in play — show the
  // authoritative "Remaining Budget" (cumulative) instead, and pair it with
  // "After Purchase" (remainingAfterThisOrder) rather than re-showing the
  // same "Remaining" figure twice.
  const tiles = [
    ["Remaining Budget", ctx.remaining],
    ["Committed", ctx.committed],
    ["Approved – Unpaid", ctx.approvedUnpaid],
    ["Approved – Paid", ctx.approvedPaid],
    ["After Purchase", ctx.remainingAfterThisOrder],
  ]

  return (
    <SectionCard
      title="Budget Context"
      subtitle="What this decision spends against."
      icon="account_balance_wallet"
    >
      <MDBox
        display="grid"
        gap={2}
        mb={2.5}
        sx={{ gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" } }}
      >
        {tiles.map(([label, value]) => (
          <MDBox key={label}>
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {label}
            </MDTypography>
            <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
              {formatVND(value)}
            </MDTypography>
            {label === "Remaining Budget" && ctx.accumulatedThroughMonth != null && (
              <MDTypography variant="caption" color="text" display="block">
                Accumulated through month {ctx.accumulatedThroughMonth}
              </MDTypography>
            )}
          </MDBox>
        ))}
      </MDBox>

      <MDProgress value={usedPct} color={healthColor} />

      <MDBox
        mt={2}
        p={2}
        borderRadius="lg"
        sx={{ backgroundColor: over ? "#fef2f2" : "#eef2ff" }}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <MDTypography variant="button" fontWeight="medium">
          Remaining after approving this order
        </MDTypography>
        <MDTypography variant="h6" fontWeight="bold" color={over ? "error" : "info"}>
          {after < 0 ? "-" : ""}
          {formatVND(Math.abs(after))}
        </MDTypography>
      </MDBox>

      {over && (
        <MDBox mt={1.5} display="flex" alignItems="flex-start" gap={1} color="error">
          <Icon fontSize="small">warning</Icon>
          <MDTypography variant="caption" color="error" fontWeight="medium">
            This budget is over-spent by {formatVND(overSpent)}.
          </MDTypography>
        </MDBox>
      )}
    </SectionCard>
  )
}

// ── Vendor Proposals ─────────────────────────────────────────────────────────
// `lines` (optional) is the order's own lines — a proposal's items only carry
// itemId/proposedAmount/note (no quantity), so quantity is looked up from the
// matching order line by itemId to show alongside each quoted item.
// `sourceId` (optional, order id or PO id depending on `sourceType`, default
// "order"): shows a "Review and compare quotation" button so the approver
// can re-pick the winning vendor right here, before deciding — same
// VendorComparisonModal the requester's own create-order/create-PO form
// uses. For sourceType="purchase" this also recalculates the PO's total and
// budget commit server-side once saved (see selectVendorProposal).
// `locked` (PO already approved+): BE rejects select with 409, so disable the
// compare/select button instead. `onRefresh`/`refreshing` (optional) add a
// Refresh button for pages that read proposals off a detail response.
export const VendorProposalsSection = ({
  proposals,
  lines,
  sourceType = "order",
  sourceId,
  locked = false,
  onRefresh,
  refreshing = false,
  onSelected,
}) => {
  const rows = proposals || []
  const totals = rows.map(proposalTotal).filter((n) => n > 0)
  const cheapest = totals.length ? Math.min(...totals) : null
  const quantityByItemId = {}
  ;(lines || []).forEach((l) => {
    if (l.itemId != null) quantityByItemId[l.itemId] = l.quantity
  })
  const [compareOpen, setCompareOpen] = useState(false)
  const hasQuotes = rows.some(hasQuote)
  const refined = useRefined()

  const columns = [
    {
      Header: "Supplier",
      accessor: "supplierName",
      Cell: ({ row }) => (
        <MDBox>
          <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <MDTypography variant="button" fontWeight="medium">
              {row.original.supplierName || row.original.supplier?.name || "—"}
            </MDTypography>
            {row.original.isSelected && <StatusBadge status="approved" label="Selected" />}
          </MDBox>
          {row.original.proposalTaxCode && (
            <MDTypography variant="caption" color="text" display="block">
              MST báo giá: {row.original.proposalTaxCode}
            </MDTypography>
          )}
          {row.original.supplier?.email && (
            <MDTypography variant="caption" color="text" display="block">
              {row.original.supplier.email}
            </MDTypography>
          )}
          <ProposalHistoryDialog
            proposal={row.original}
            sourceType={sourceType}
            sourceId={sourceId}
            canWrite={!locked}
          />
        </MDBox>
      ),
    },
    {
      Header: "Items quoted",
      accessor: "items",
      Cell: ({ row }) =>
        !hasQuote(row.original) ? (
          <MDTypography variant="caption" color="text">
            {NO_QUOTE_LABEL}
          </MDTypography>
        ) : (
          quotedItems(row.original).map((it) => (
            <MDTypography key={it.id ?? it.itemId} variant="caption" color="text" display="block">
              {it.itemName}
              {quantityByItemId[it.itemId] != null ? ` × ${quantityByItemId[it.itemId]}` : ""}
              {it.unitPrice != null ? ` · ${formatVND(it.unitPrice)}/đv` : ""}
              {it.note ? ` — ${it.note}` : ""}
              {formatWarranty(it) ? ` · ${formatWarranty(it)}` : ""}
              {PRICE_SOURCE_LABEL[it.priceSource] ? ` · ${PRICE_SOURCE_LABEL[it.priceSource]}` : ""}
            </MDTypography>
          ))
        ),
    },
    {
      Header: "Total",
      accessor: "total",
      align: "right",
      Cell: ({ row }) => {
        const total = proposalTotal(row.original)
        const diff = cheapest != null && total > 0 ? total - cheapest : null
        return (
          <MDBox textAlign="right">
            {hasQuote(row.original) && (
              <MDTypography variant="button" fontWeight="bold">
                {formatVND(total)}
              </MDTypography>
            )}
            {row.original.deliveryFee != null && (
              <MDTypography variant="caption" display="block" color="text">
                + phí giao {formatVND(row.original.deliveryFee)} (không cộng vào tổng)
              </MDTypography>
            )}
            {rows.length >= 2 && diff != null && (
              <MDTypography
                variant="caption"
                display="block"
                color={diff === 0 ? "success" : "warning"}
                fontWeight="medium"
              >
                {diff === 0 ? "Lowest" : formatVNDSigned(diff)}
              </MDTypography>
            )}
          </MDBox>
        )
      },
    },
    {
      Header: "Link",
      accessor: "url",
      Cell: ({ row }) => (
        <div className="pc-root" style={{ minHeight: 0, background: "transparent" }}>
          <LinkActions link={proposalLink(row.original)} />
        </div>
      ),
    },
    {
      Header: "Deadline",
      accessor: "expiredAt",
      Cell: ({ row, value }) => (
        <MDBox>
          <MDTypography variant="caption" color="text" display="block">
            {value ? formatDate(value) : "—"}
          </MDTypography>
          {row.original.url && (
            <ProposalDeadlineActions
              proposal={row.original}
              sourceType={sourceType}
              sourceId={sourceId}
              disabled={locked}
            />
          )}
        </MDBox>
      ),
    },
  ]

  return (
    <SectionCard
      title="Vendor Proposals"
      icon="storefront"
      subtitle={refined ? "Compare quotations from suppliers" : undefined}
      action={
        refined ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {onRefresh && (
              <Button size="sm" variant="ghost" disabled={refreshing} onClick={onRefresh}>
                <Icon>refresh</Icon>
                Refresh
              </Button>
            )}
            {sourceId && (
              <Button
                size="sm"
                variant="secondary"
                disabled={!hasQuotes || locked}
                onClick={() => setCompareOpen(true)}
                title={locked ? "Proposals are locked once the PO is approved." : undefined}
              >
                Review and compare quotation
              </Button>
            )}
          </div>
        ) : (
        <MDBox display="flex" gap={1}>
          {onRefresh && (
            <MDButton size="small" variant="text" color="info" disabled={refreshing} onClick={onRefresh}>
              <Icon sx={{ mr: 0.5 }}>refresh</Icon>
              Refresh
            </MDButton>
          )}
          {sourceId && (
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              disabled={!hasQuotes || locked}
              onClick={() => setCompareOpen(true)}
              title={locked ? "Proposals are locked once the PO is approved." : undefined}
            >
              Review and compare quotation
            </MDButton>
          )}
        </MDBox>
        )
      }
    >
      {sourceId && (
        // VendorComparisonModal is built on the Requester design system
        // (`.pc-root` custom properties, see ui/tokens.css) — this section
        // renders on the MUI-based Manager review page, which has no
        // `.pc-root` ancestor, so wrap it here (same fix as
        // OrderReviewContainer.js's DeliveryConfirmationCard wrapper).
        <div className="pc-root" style={{ minHeight: 0, background: "transparent" }}>
          <VendorComparisonModal
            open={compareOpen}
            onClose={() => setCompareOpen(false)}
            proposals={rows}
            sourceType={sourceType}
            sourceId={sourceId}
            onSelected={onSelected}
          />
        </div>
      )}
      {rows.length === 0 ? (
        refined ? (
          <p className="pc-section-empty">
            <span className="pc-section-empty-title">No vendor proposals attached.</span>
            Quotations from suppliers will appear here.
          </p>
        ) : (
          <MDTypography variant="body2" color="text">
            No vendor proposals attached.
          </MDTypography>
        )
      ) : (
        <ScopedDataTable columns={columns} rows={rows} />
      )}
    </SectionCard>
  )
}

// ── Order Lines ──────────────────────────────────────────────────────────────
// Same data the create/edit form shows (item × center × qty), read-only here.
// `centerNameById` is optional; falls back to a bare "#<id>" label.
export const OrderLinesSection = ({ lines, centerNameById }) => {
  const rows = lines || []
  const showMonth = rows.some((l) => linePeriodLabel(l))

  const columns = [
    {
      Header: "Item",
      accessor: "itemName",
      // Off-catalog lines (itemId null) carry their name in
      // `offCatalogItemName`, not `itemName` — same fallback as OrderLines
      // (OrderCards.js) so both read-only line tables agree.
      Cell: ({ row }) => {
        const l = row.original
        return (
          <MDBox>
            <MDTypography variant="button" fontWeight="medium" component="div">
              <NoteCell text={l.itemName || l.offCatalogItemName} hover minWidth={240} maxWidth={360} />
            </MDTypography>
            {!l.itemId && l.offCatalogItemName && (
              <MDTypography variant="caption" color="text" display="block">
                Off-catalog{l.offCatalogUnit ? ` · ${l.offCatalogUnit}` : ""}
              </MDTypography>
            )}
            {l.requestType === "replacement" && l.replacedAssetCode && (
              <MDTypography variant="caption" color="text" display="block">
                Replaces {l.replacedAssetCode}
              </MDTypography>
            )}
          </MDBox>
        )
      },
    },
    {
      Header: "Center",
      accessor: "centerId",
      Cell: ({ value }) => centerNameById?.[value] || `#${value}`,
    },
    ...(showMonth
      ? [
          {
            Header: "Month",
            accessor: "fiscalMonth",
            Cell: ({ row }) => (
              <span style={{ whiteSpace: "nowrap", color: LINE_PERIOD_COLOR[row.original.period?.budgetStatus] }}>
                {linePeriodLabel(row.original) || "—"}
              </span>
            ),
          },
        ]
      : []),
    {
      Header: "Type",
      accessor: "requestType",
      Cell: ({ value }) => <span style={{ textTransform: "capitalize" }}>{value || "new"}</span>,
    },
    { Header: "Qty", accessor: "quantity", align: "right" },
    {
      Header: "Unit price",
      accessor: "unitPrice",
      align: "right",
      Cell: ({ value }) => formatVND(value),
    },
    {
      Header: "Amount",
      accessor: "amount",
      align: "right",
      Cell: ({ value }) => formatVND(value),
    },
    {
      Header: "Note",
      accessor: "note",
      Cell: ({ row }) => {
        const l = row.original
        return (
          <MDBox>
            <MDTypography variant="body2" color="text" component="div">
              <NoteCell text={l.note} />
            </MDTypography>
            {l.ticketLink && (
              <MDTypography
                variant="caption"
                color="info"
                component="a"
                href={l.ticketLink}
                target="_blank"
                rel="noopener noreferrer"
                display="block"
              >
                Ticket ↗
              </MDTypography>
            )}
            {l.proofImageUrl && (
              <MDTypography
                variant="caption"
                color="info"
                component="a"
                href={l.proofImageUrl}
                target="_blank"
                rel="noopener noreferrer"
                display="block"
              >
                Proof photo ↗
              </MDTypography>
            )}
          </MDBox>
        )
      },
    },
  ]

  return (
    <SectionCard title="Order Lines" icon="list_alt">
      {rows.length === 0 ? (
        <MDTypography variant="body2" color="text">
          No lines on this order.
        </MDTypography>
      ) : (
        <ScopedDataTable columns={columns} rows={rows} />
      )}
    </SectionCard>
  )
}

// ── Linked Purchase Orders ───────────────────────────────────────────────────
// `purchases[]` is always an array (never null). An order can end up with
// more than one PO (e.g. split across vendors), so this is informational.
export const LinkedPurchasesSection = ({ purchases }) => {
  const rows = purchases || []
  if (rows.length === 0) return null

  const columns = [
    { Header: "PO Code", accessor: "code" },
    { Header: "Vendor", accessor: "vendorName" },
    {
      Header: "Total",
      accessor: "totalAmount",
      align: "right",
      Cell: ({ value }) => formatVND(value),
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => <StatusBadge status={value} />,
    },
  ]

  return (
    <SectionCard title="Linked Purchase Orders" icon="shopping_cart">
      <ScopedDataTable columns={columns} rows={rows} />
    </SectionCard>
  )
}

// ── Attachments ───────────────────────────────────────────────────────────
export const AttachmentsSection = ({ documents }) => {
  const docs = documents || []
  return (
    <SectionCard title="Attachments" icon="attach_file">
      {docs.length === 0 ? (
        <MDTypography variant="body2" color="text">
          No documents attached.
        </MDTypography>
      ) : (
        <MDBox display="flex" flexDirection="column" gap={1}>
          {docs.map((d, i) => (
            <MDBox
              key={d.id ?? i}
              component="a"
              href={d.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              display="flex"
              alignItems="center"
              gap={1.5}
              p={1.5}
              borderRadius="lg"
              bgColor="grey-100"
              sx={{ textDecoration: "none", "&:hover": { backgroundColor: "grey.200" } }}
            >
              <MDBox
                width="2.25rem"
                height="2.25rem"
                minWidth="2.25rem"
                borderRadius="lg"
                bgColor="white"
                color="info"
                display="flex"
                alignItems="center"
                justifyContent="center"
                shadow="sm"
              >
                <Icon fontSize="small">{DOC_ICON[d.docType] || "description"}</Icon>
              </MDBox>
              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {d.fileName || "Document"}
                </MDTypography>
                <MDTypography variant="caption" color="text" textTransform="capitalize">
                  {(d.docType || "file").replace(/_/g, " ")}
                </MDTypography>
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
      )}
    </SectionCard>
  )
}

const ACTION_VERB = {
  approve: "approved",
  reject: "rejected",
  info_request: "requested more info",
  resubmit: "resubmitted",
  submit: "submitted",
  reroute: "rerouted",
}

// Real API gives just `quorum` (a count), not a required/of pair — render it
// with the same "needs N approvers" phrasing used elsewhere.
const quorumText = (step) => {
  const parts = []
  if (step.mode) parts.push(approvalModeLabel(step.mode))
  if (step.quorum) parts.push(formatQuorum(step.quorum))
  return parts.join(" · ")
}

// ── Approval Progress ────────────────────────────────────────────────────────
export const ApprovalProgressSection = ({ steps }) => {
  const list = steps || []
  return (
    <SectionCard title="Approval Progress" icon="alt_route" mb={0}>
      {list.length === 0 ? (
        <MDTypography variant="body2" color="text">
          No approval route resolved yet.
        </MDTypography>
      ) : (
        <MDBox>
          {list.map((step, idx) => {
            const isLast = idx === list.length - 1
            const color = step.state === "done" ? "success" : step.state === "current" ? "info" : "secondary"
            const icon = step.state === "done" ? "check" : step.state === "current" ? "hourglass_top" : "radio_button_unchecked"
            const approverList = step.approverNames || step.approvers
            const approvers = Array.isArray(approverList)
              ? approverList.join(", ")
              : approverList || "—"
            const meta = quorumText(step)
            // TimelineItem's `description` is a plain string, so multiple
            // actions on one step are folded into a single readable line
            // rather than nested markup.
            const description = (step.actions || [])
              .map((a) => {
                const verb = a.action || a.type
                const who = `${a.actor || "Someone"} ${ACTION_VERB[verb] || verb}`
                const when = (a.actedAt || a.at) ? ` · ${formatDate(a.actedAt || a.at)}` : ""
                const note = a.note ? ` — “${a.note}”` : ""
                return `${who}${when}${note}`
              })
              .join("  •  ")
            return (
              <TimelineItem
                key={step.stepOrder ?? step.step ?? idx}
                color={color}
                icon={icon}
                title={approvers}
                dateTime={meta}
                lastItem={isLast}
                description={description}
              />
            )
          })}
        </MDBox>
      )}
    </SectionCard>
  )
}
